/**
 * The clock (wave C, decision 0071, spec §7; decision C2: one clock, in the Project DO). Monitors people set (or accept
 * from an agent's `propose_monitor` card), recurring series coming due, and the daily check share the Project DO's one
 * alarm: core `nextAlarm` takes the earliest of {@link nextMonitorFireAt}, {@link nextSeriesDueAt} and
 * {@link nextDailyCheckAt}; on the alarm the DO's clock pass sends FireMonitor for {@link dueMonitors}, NoteSeriesDue for
 * {@link dueSeries} and, at the daily check's time, NoteLooseEnds when `looseEndsDelta` says so. The cron parser is
 * `nextCronAt` in `recurrence.ts` (one parser); this module adds minute deduplication, quiet hours and optional budgets for
 * timed turns. Missed fires are skipped, never replayed: a fire is due only within {@link MISSED_GRACE_MS} of its time.
 * Pure.
 */
import type { MonitorQuiet, MonitorWhen } from "@minga/protocol"
import { DEFAULT_TIMED_CAP } from "@minga/protocol"
import { isWorkingMember } from "./multiplayer"
import { isInPlanning } from "./pages"
import { openOccurrenceOf } from "./readiness"
import { instantOfWall, isTimeZone, nextCronAt, parseCron, periodOf, wallTimeOf } from "./recurrence"
import { agentOf, isOpenItem } from "./state"
import { childrenOf } from "./tree"
import type { Agent, AgentId, Ids, Item, ItemId, Monitor, ProjectState, UserId } from "./types"

export { DEFAULT_TIMED_CAP } from "@minga/protocol"

/** Cron's natural minute resolution prevents the same fire being delivered twice. */
export const MONITOR_FLOOR_MS = 60_000

/** "Every N hours" is quiet 20:00 to 08:00 in the project's zone unless a person sets None. */
export const DEFAULT_QUIET: MonitorQuiet = {
  from: "20:00" as MonitorQuiet["from"],
  to: "08:00" as MonitorQuiet["to"],
}

/** The daily check's hour when `Cadence.dailyCheckHour` is absent. */
export const DEFAULT_DAILY_CHECK_HOUR = 9

/**
 * A fire (or a series' due time) older than this at the clock pass was missed (the DO slept through it): it is skipped,
 * never replayed. The alarm is set for the exact time, so a live DO is never this late.
 */
export const MISSED_GRACE_MS = 3_600_000

/**
 * The cron of a `when`, in the three shapes people set: every N hours (`0 * * * *`, `0 *\/3 * * *`), every day at
 * HH:MM (`30 9 * * *`), every week on a weekday at HH:MM (`0 9 * * 1`). `tz` is carried beside it in the trigger.
 */
export const cronOfWhen = (when: MonitorWhen): string => {
  switch (when._tag) {
    case "cron":
      return when.expr
    case "every_hours":
      return when.n === 1 ? "0 * * * *" : `0 */${when.n} * * *`
    case "daily": {
      const [h, m] = when.at.split(":")
      return `${Number(m)} ${Number(h)} * * *`
    }
    case "weekly": {
      const [h, m] = when.at.split(":")
      return `${Number(m)} ${Number(h)} * * ${when.weekday}`
    }
  }
}

/** The monitor's `trigger` for a `when` in the project's zone. */
export const triggerOfWhen = (when: MonitorWhen, tz: string): Monitor["trigger"] => ({
  _tag: "cron",
  expr: cronOfWhen(when),
  tz,
})

// #region zone and quiet hours
/** The project's time zone (`Project.cadence.timezone`), UTC when unset or unknown. */
export const projectZone = (s: ProjectState): string => {
  const tz = s.project?.cadence.timezone
  return tz && isTimeZone(tz) ? tz : "UTC"
}

/** The date (yyyy-mm-dd) of `at` in the project's zone: the timed-turn counter's day. */
export const projectDay = (s: ProjectState, at: number): string => periodOf(at, projectZone(s))

const minutesOf = (hhmm: string): number | null => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm)
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

/** Is `at` inside the quiet hours (`from` inclusive, `to` exclusive; overnight when `from` > `to`)? */
export const inQuietHours = (at: number, quiet: Monitor["quietHours"]): boolean => {
  if (quiet === null) return false
  const from = minutesOf(quiet.from)
  const to = minutesOf(quiet.to)
  if (from === null || to === null || from === to) return false
  const w = wallTimeOf(at, quiet.tz)
  const t = w.hour * 60 + w.minute
  return from < to ? t >= from && t < to : t >= from || t < to
}

/** The stored quiet hours of a `when`: the given ones (null = none), else the default for every-N-hours. */
export const quietOfWhen = (
  when: MonitorWhen,
  quiet: MonitorQuiet | null | undefined,
  tz: string,
): Monitor["quietHours"] => {
  const q = quiet === undefined ? (when._tag === "every_hours" ? DEFAULT_QUIET : null) : quiet
  return q === null ? null : { from: q.from, to: q.to, tz }
}

/** Why a schedule can't be set, or null: cron only, valid, in a known zone. */
export const triggerProblem = (trigger: Monitor["trigger"]): string | null => {
  if (trigger._tag !== "cron") return `a ${trigger._tag} trigger is not in this release: use a schedule`
  if (!isTimeZone(trigger.tz)) return `${trigger.tz} is not a time zone`
  const c = parseCron(trigger.expr)
  if (!c) return "a schedule is a 5-field cron: minute hour day-of-month month day-of-week"
  return null
}
// #endregion zone and quiet hours

// #region fire times
const MAX_SKIPS = 2_000

/** The first fire at or after `from` outside quiet hours (null: never). */
const fireAtOrAfter = (
  expr: string,
  tz: string,
  quiet: Monitor["quietHours"],
  from: number,
): number | null => {
  let f = nextCronAt(expr, tz, from - 1)
  for (let i = 0; f !== null && inQuietHours(f, quiet) && i < MAX_SKIPS; i++) f = nextCronAt(expr, tz, f)
  return f !== null && inQuietHours(f, quiet) ? null : f
}

/**
 * The next fire time at or after `max(lastFiredAt + MONITOR_FLOOR_MS, now)`, outside quiet hours; null for a trigger C
 * does not fire (`at`, `webhook`, `kpi_threshold`) or a cron that never matches.
 */
export const nextFireAt = (
  trigger: Monitor["trigger"],
  quiet: Monitor["quietHours"],
  lastFiredAt: number | null,
  now: number,
): number | null => {
  if (trigger._tag !== "cron") return null
  const from = lastFiredAt === null ? now : Math.max(lastFiredAt + MONITOR_FLOOR_MS, now)
  return fireAtOrAfter(trigger.expr, trigger.tz, quiet, from)
}

/**
 * The fire due at `now`: the latest fire time in `[max(lastFiredAt + floor, now − MISSED_GRACE_MS), now]`, null when
 * there is none. Earlier missed fires are skipped.
 */
export const dueFireAt = (
  trigger: Monitor["trigger"],
  quiet: Monitor["quietHours"],
  lastFiredAt: number | null,
  now: number,
): number | null => {
  if (trigger._tag !== "cron") return null
  const from = Math.max(lastFiredAt === null ? 0 : lastFiredAt + MONITOR_FLOOR_MS, now - MISSED_GRACE_MS)
  let f = fireAtOrAfter(trigger.expr, trigger.tz, quiet, from)
  if (f === null || f > now) return null
  for (let i = 0; i < MAX_SKIPS; i++) {
    const g = fireAtOrAfter(trigger.expr, trigger.tz, quiet, f + MONITOR_FLOOR_MS)
    if (g === null || g > now) break
    f = g
  }
  return f
}

/** Is `at` one of the trigger's fire times (a cron minute outside quiet hours)? */
export const isFireTime = (trigger: Monitor["trigger"], quiet: Monitor["quietHours"], at: number): boolean =>
  trigger._tag === "cron" && nextCronAt(trigger.expr, trigger.tz, at - 1) === at && !inQuietHours(at, quiet)
// #endregion fire times

// #region status
/** The project's monitors (absent = none). */
export const monitorsOf = (s: ProjectState): ReadonlyArray<Monitor> => s.monitors ?? []

export const monitorOf = (s: ProjectState, id: Ids["MonitorId"]): Monitor | undefined =>
  monitorsOf(s).find((m) => m.id === id)

const nameOf = (s: ProjectState, userId: UserId): string =>
  s.members.find((m) => m.userId === userId)?.displayName ?? "Its setter"

/**
 * Why the monitor's setter can no longer start its turns (spec §7.3): "Becky left the project", "Becky is a viewer
 * now"; null while the setter is a working member.
 */
export const setterGone = (s: ProjectState, m: Pick<Monitor, "ownerId">): string | null => {
  if (isWorkingMember(s, m.ownerId)) return null
  const member = s.members.find((x) => x.userId === m.ownerId)
  return member ? `${member.displayName} is a viewer now` : `${nameOf(s, m.ownerId)} left the project`
}

/** How a monitor stands: firing, paused by a person, or blocked (its setter left or became a viewer; a failed turn). */
export type MonitorStatus =
  | { readonly _tag: "active" }
  | { readonly _tag: "paused" }
  | { readonly _tag: "blocked"; readonly reason: string }

/** Blocked wins (stored, or derived: a setter who left shows at once, before the clock writes it). */
export const monitorStatus = (s: ProjectState, m: Monitor): MonitorStatus => {
  if (m.state._tag === "blocked") return { _tag: "blocked", reason: m.state.reason }
  const gone = setterGone(s, m)
  if (gone !== null) return { _tag: "blocked", reason: gone }
  return m.enabled ? { _tag: "active" } : { _tag: "paused" }
}

/** The agent a monitor wakes, when it can take a turn (active). */
const wakeableAgent = (s: ProjectState, agentId: AgentId): Agent | undefined => {
  const a = agentOf(s, agentId)
  return a?.status === "active" ? a : undefined
}

/**
 * Does the clock run for this project at all? Not before genesis, and not once it is archived: an archived project
 * wakes for no daily check, monitor fire or series due time (it would otherwise wake its Durable Object every day for
 * nothing). Paused projects keep their clock (their checks still say what waits).
 */
export const clockRuns = (s: ProjectState): boolean => s.project != null && s.project.status !== "archived"

/** Monitors the clock looks at: stored active, enabled, a cron, and their agent active (none once archived). */
const ticking = (s: ProjectState): ReadonlyArray<Monitor> =>
  !clockRuns(s)
    ? []
    : monitorsOf(s).filter(
        (m) =>
          m.state._tag === "active" &&
          m.enabled &&
          m.trigger._tag === "cron" &&
          wakeableAgent(s, m.agentId) !== undefined,
      )

/**
 * Monitors whose fire is due at `now`, with that fire time (the FireMonitor's `at`). A monitor whose setter left is due
 * too: its FireMonitor blocks it (written once) instead of waking anyone.
 */
export const dueMonitors = (
  s: ProjectState,
  now: number,
): ReadonlyArray<{ readonly monitor: Monitor; readonly at: number }> =>
  ticking(s).flatMap((monitor) => {
    const at = dueFireAt(monitor.trigger, monitor.quietHours, monitor.lastFiredAt, now)
    return at === null ? [] : [{ monitor, at }]
  })

/** The next fire after `now` of any monitor that is ticking (for `nextAlarm`). */
export const nextMonitorFireAt = (s: ProjectState, now: number): number | null => {
  let min: number | null = null
  for (const m of ticking(s)) {
    const at = nextFireAt(m.trigger, m.quietHours, m.lastFiredAt, now + 1)
    if (at !== null && (min === null || at < min)) min = at
  }
  return min
}
// #endregion status

// #region recurring series
/** An open, unpaused series owned by an active agent (the only kind the clock wakes). */
const wakingSeries = (
  s: ProjectState,
): ReadonlyArray<Item & { readonly recurrence: NonNullable<Item["recurrence"]> }> =>
  !clockRuns(s)
    ? []
    : s.items.filter(
        (t): t is Item & { readonly recurrence: NonNullable<Item["recurrence"]> } =>
          t.recurrence !== null &&
          isOpenItem(t) &&
          !t.paused &&
          t.owner?._tag === "agent" &&
          wakeableAgent(s, t.owner.agentId) !== undefined,
      )

/**
 * The agent a series' due times wake (open, unpaused, owned by an active agent), or undefined: then nothing runs on a
 * timer and a person presses Run now. For the words on the item page and in the read tools.
 */
export const seriesWakeAgent = (s: ProjectState, t: Item): Agent | undefined =>
  t.recurrence !== null && isOpenItem(t) && !t.paused && t.owner?._tag === "agent"
    ? wakeableAgent(s, t.owner.agentId)
    : undefined

/** Has an occurrence for this period been created already (open or closed)? */
const hasOccurrenceFor = (s: ProjectState, series: Item, period: string): boolean =>
  childrenOf(s, series.id).some((c) => c.intentKey.startsWith(`occ-${period}-`))

/** Why a series wakes nobody now (null: it may wake its owner agent). */
export const seriesDueProblem = (
  s: ProjectState,
  series: Item | undefined,
  period: string,
): string | null => {
  if (!series) return "no such item"
  if (series.recurrence === null) return `${series.ref} doesn't repeat`
  if (!isOpenItem(series)) return `${series.ref} is ${series.status}`
  if (series.paused) return `${series.ref} is paused`
  // decision 0084 (§1.5 gap 2): an occurrence under Planning can never run, so the clock wakes nobody for it
  if (isInPlanning(s, series)) return `${series.ref} is in Planning`
  if (series.owner?._tag !== "agent") return `${series.ref} is a person's: Run now stays theirs`
  if (!wakeableAgent(s, series.owner.agentId)) return `${series.ref}'s agent is not active`
  const open = openOccurrenceOf(s, series)
  if (open) return `${open.ref} is still open`
  if (s.seriesDue?.[series.id] === period) return `${series.ref} was already noted due for ${period}`
  if (hasOccurrenceFor(s, series, period)) return `${series.ref} already has an occurrence for ${period}`
  return null
}

/**
 * Recurring series owned by an agent that are due at `now` (a schedule time within {@link MISSED_GRACE_MS}) for a
 * period not yet noted (`state.seriesDue`), with no open occurrence, not paused or closed: the clock pass sends
 * NoteSeriesDue for each. `period` is the due time's date in the schedule's zone (what Run now names the occurrence).
 */
export const dueSeries = (
  s: ProjectState,
  now: number,
): ReadonlyArray<{ readonly itemId: ItemId; readonly period: string; readonly at: number }> =>
  wakingSeries(s).flatMap((t) => {
    const { cron, tz } = t.recurrence.schedule
    let at = nextCronAt(cron, tz, now - MISSED_GRACE_MS - 1)
    if (at === null || at > now) return []
    for (let i = 0; i < MAX_SKIPS; i++) {
      const g = nextCronAt(cron, tz, at)
      if (g === null || g > now) break
      at = g
    }
    const period = periodOf(at, tz)
    return seriesDueProblem(s, t, period) === null ? [{ itemId: t.id, period, at }] : []
  })

/** The next schedule time after `now` of a series that could wake its agent (for `nextAlarm`). */
export const nextSeriesDueAt = (s: ProjectState, now: number): number | null => {
  let min: number | null = null
  for (const t of wakingSeries(s)) {
    if (openOccurrenceOf(s, t)) continue
    const at = nextCronAt(t.recurrence.schedule.cron, t.recurrence.schedule.tz, now)
    if (at !== null && (min === null || at < min)) min = at
  }
  return min
}
// #endregion recurring series

// #region daily check
/** The daily check's hour, or null when it is off (`Cadence.dailyCheckHour`: absent = 9, null = off). */
export const dailyCheckHourOf = (s: ProjectState): number | null => {
  const h = s.project?.cadence.dailyCheckHour
  return h === undefined ? DEFAULT_DAILY_CHECK_HOUR : h
}

/**
 * The next daily check after `now` (at `dailyCheckHour` in the project's zone); null when it is off, before genesis or
 * once the project is archived.
 */
export const nextDailyCheckAt = (s: ProjectState, now: number): number | null => {
  const hour = dailyCheckHourOf(s)
  if (hour === null || !clockRuns(s)) return null
  return nextCronAt(`0 ${hour} * * *`, projectZone(s), now)
}

/**
 * The most recent daily check time at or before `now` (null when it is off): the clock pass runs the check when this is
 * later than the last time it ran it.
 */
export const latestDailyCheckAt = (s: ProjectState, now: number): number | null => {
  const hour = dailyCheckHourOf(s)
  if (hour === null || !clockRuns(s)) return null
  const tz = projectZone(s)
  const w = wallTimeOf(now, tz)
  for (let back = 0; back < 3; back++) {
    const d = new Date(Date.UTC(w.year, w.month - 1, w.day - back))
    const at = instantOfWall(tz, d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), hour, 0)
    if (at !== null && at <= now) return at
  }
  return null
}
// #endregion daily check

// #region the daily cap
/** The agent's cap of timed turns a day (`Agent.timedCap`; null or absent = no cap). */
export const timedCapOf = (a: Pick<Agent, "timedCap">): number | null => a.timedCap ?? DEFAULT_TIMED_CAP

/** Timed turns the agent got on `day`, and whether a person lifted the cap for that day (Continue on its card). */
export const timedTurnsOn = (
  s: ProjectState,
  agentId: AgentId,
  day: string,
): { readonly count: number; readonly extra: boolean } => {
  const e = s.timedTurns?.[agentId]
  return e && e.day === day ? { count: e.count, extra: e.extra } : { count: 0, extra: false }
}

/** Would one more timed turn on `day` go over the agent's cap (and wait on the `timed:<agentId>:<day>` card)? */
export const timedCapReached = (s: ProjectState, agent: Agent, day: string): boolean => {
  const t = timedTurnsOn(s, agent.id, day)
  const cap = timedCapOf(agent)
  return cap !== null && !t.extra && t.count >= cap
}

/** The intent key of the day's cap card. */
export const timedIntentKey = (agentId: AgentId, day: string): string => `timed:${agentId}:${day}`
// #endregion the daily cap

/**
 * The monitor an agent's `propose_monitor` card carries (spec §7.3): kind routine, `trigger: cron` in the project's
 * zone, quiet hours as given (absent: 20:00-08:00 for every-N-hours), `ifActive: coalesce`, set by `ownerId` (the
 * card's recipient; Accept makes the person who decides the setter).
 */
export const monitorOfProposal = (
  s: ProjectState,
  input: {
    readonly monitorId: Ids["MonitorId"]
    readonly agentId: AgentId
    readonly ownerId: UserId
    readonly instruction: string
    readonly when: MonitorWhen
    readonly quietHours?: MonitorQuiet | null
  },
): Monitor => {
  const tz = projectZone(s)
  return {
    id: input.monitorId,
    ownerId: input.ownerId,
    visibility: "workspace",
    agentId: input.agentId,
    owner: { _tag: "agent", agentId: input.agentId },
    projectId: s.project?.id ?? null,
    kind: "routine",
    trigger: triggerOfWhen(input.when, tz),
    subject: null,
    instruction: input.instruction,
    ifActive: "coalesce",
    quietHours: quietOfWhen(input.when, input.quietHours, tz),
    state: { _tag: "active" },
    createdByRoutine: null,
    enabled: true,
    lastFiredAt: null,
  }
}
